import { Button } from '@mui/material';
import { Box } from '@mui/system';
import deepEqual from 'fast-deep-equal';
import { toClassName } from 'helpers/toClassName';
import React, { Dispatch } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { updateRiskMatrixAction } from 'redux/actions/riskActions';
import { AsyncAction } from 'redux/asyncAction';
import {
  restoreOriginalRiskMatrix,
  restoreOriginalSymbolLimitsRows,
  riskSelector,
} from 'redux/reducers/riskReducer';
import { AssetClassView } from 'routes/Risk/components/AssetClassView';
import classes from 'routes/Risk/risk.module.scss';
import { RiskMatrix } from 'types/risk';

interface Props {
  readonly matrix: RiskMatrix | null;
  readonly readOnly: boolean;
}

export const Matrix: React.FC<Props> = (props: Props): React.ReactElement => {
  const risk = useSelector(riskSelector);
  const dispatch = useDispatch<Dispatch<AsyncAction | AnyAction>>();

  const { matrix, readOnly } = props;
  const { options, equities } = matrix ?? {};
  const modified = React.useMemo(
    (): boolean => risk.matrix !== risk.originalMatrix,
    [risk.matrix, risk.originalMatrix]
  );

  const disableSubmit = React.useMemo((): boolean => {
    if (readOnly) {
      return true;
    }

    return risk.savingRiskMatrix || deepEqual(risk.matrix, risk.originalMatrix);
  }, [readOnly, risk.matrix, risk.originalMatrix, risk.savingRiskMatrix]);

  const handleReset = React.useCallback((): void => {
    if (readOnly) {
      return;
    }

    dispatch(restoreOriginalRiskMatrix());
    dispatch(restoreOriginalSymbolLimitsRows());
  }, [dispatch, readOnly]);

  const handleSubmit = React.useCallback((): void => {
    if (readOnly) {
      return;
    }

    dispatch(updateRiskMatrixAction(risk.matrix));
  }, [dispatch, readOnly, risk.matrix]);

  if (matrix === null) {
    return <div className={classes.matrix} />;
  } else {
    return (
      <div
        className={toClassName(classes.matrix, {
          [classes.savingMatrix]: risk.savingRiskMatrix,
        })}
      >
        <AssetClassView label="Options" group={options} type="options" readOnly={readOnly} />
        <AssetClassView label="Equities" group={equities} type="equities" readOnly={readOnly} />
        <Box className={classes.buttonsContainer}>
          <Button onClick={handleReset} disabled={readOnly || !modified}>
            Reset
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={disableSubmit}>
            Submit
          </Button>
        </Box>
      </div>
    );
  }
};
